"use client";

import React, { useState, KeyboardEvent } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}

export function TagInput({ value = [], onChange, placeholder }: TagInputProps) {
  const [inputValue, setInputValue] = useState("");

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !value.includes(trimmedTag)) {
      onChange([...value, trimmedTag]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Check for comma-separated values
    if (newValue.includes(",")) {
      const tags = newValue
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag);
      tags.forEach((tag) => addTag(tag));
      setInputValue("");
    }
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
      setInputValue("");
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, value.length - 1));
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter((tag) => tag !== tagToRemove));
  };

  return (
    <div className="space-y-1 w-full">
      {/* Tags display */}
      <div className="flex flex-wrap gap-1 min-h-[22px] p-0.5 max-h-20 overflow-y-auto">
        {value.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className="flex items-center gap-0.5 px-1 py-0 text-[10px] leading-tight max-w-full"
          >
            <span className="truncate">{tag}</span>
            <button
              type="button"
              className="ml-0.5 rounded-full outline-none hover:bg-card-300 transition-colors flex-shrink-0"
              onClick={() => removeTag(tag)}
              aria-label={`Remove tag ${tag}`}
            >
              <X className="h-2 w-2" />
            </button>
          </Badge>
        ))}
        {value.length === 0 && (
          <div className="text-xs text-muted-foreground flex items-center gap-0.5">
            <Plus className="h-2.5 w-2.5" />
            No tags added yet
          </div>
        )}
      </div>

      {/* Input field with helper text */}
      <div className="space-y-1">
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder || "Type and press Enter or comma to add tags..."}
          className="w-full"
        />
        <div className="text-[10px] text-muted-foreground flex items-center gap-0.5">
          <Plus className="h-2 w-2" />
          Press Enter or comma to add tags
        </div>
      </div>
    </div>
  );
}
